import type { IconDefinition } from '@fortawesome/free-brands-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons/faDiscord';
import { faTelegramPlane } from '@fortawesome/free-brands-svg-icons/faTelegramPlane';
import { faTwitch } from '@fortawesome/free-brands-svg-icons/faTwitch';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { type FC, useMemo } from 'react';

import { type DealerBoothPublicDto, DealerPrimaryContactMethod } from '@furality/fox-client/models';
import { Button } from '@furality/furality-purpose';
import { getExternalRedirectLink } from '@/components/ExternalRedirectLink.tsx';
import FontAwesomeIcon from '@/components/FontAwesomeIcon.tsx';

import { getContactMethodUrl, useUpdateDealerMetrics } from './ContactMethod.helpers.ts';

export const contactMethodIcons: Record<DealerPrimaryContactMethod, IconDefinition> = {
  [DealerPrimaryContactMethod.Email]: faEnvelope,
  [DealerPrimaryContactMethod.Telegram]: faTelegramPlane,
  [DealerPrimaryContactMethod.Discord]: faDiscord,
  [DealerPrimaryContactMethod.Twitter]: faTwitter,
  [DealerPrimaryContactMethod.Twitch]: faTwitch,
  [DealerPrimaryContactMethod.Youtube]: faYoutube,
  [DealerPrimaryContactMethod.Website]: faLink,
};

type ContactMethodProps = {
  /**
   * The specific contact method to display (Twitter, Twitch, etc).
   */
  contactMethod: DealerPrimaryContactMethod;

  /**
   * The dealer of the contact method.
   */
  dealer: DealerBoothPublicDto;
};

/**
 * Given a dealer and a contact method enum, return a link with the correct icon to link to that contact method.
 */
const ContactMethod: FC<ContactMethodProps> = ({ dealer, contactMethod }) => {
  const contactMethodUrl = useMemo(
    () => getContactMethodUrl(dealer, contactMethod),
    [dealer, contactMethod],
  );

  // If the link is an email, concatenate with "mailto:".
  // otherwise, transform the url into a "You are leaving the F.O.X. Portal" redirect.
  const contactMethodUrlEnhanced = useMemo(() => {
    if (contactMethodUrl === null) {
      return '#'; // unknown contact method, this case shouldn't happen
    }

    if (contactMethod === DealerPrimaryContactMethod.Email) {
      return `mailto:${contactMethodUrl}`;
    }

    return getExternalRedirectLink({
      hasMatureContent: dealer.matureContent,
      href: contactMethodUrl,
    });
  }, [contactMethodUrl, contactMethod, dealer.matureContent]);

  const isPrimaryContactMethod =
    dealer.primaryContactMethod?.toLowerCase() === contactMethod.toLowerCase();

  const updateDealerMetrics = useUpdateDealerMetrics();

  if (contactMethodUrl == null) {
    return null;
  }

  return (
    <Button
      as="a"
      href={contactMethodUrlEnhanced}
      target="_blank"
      rel="noreferrer"
      variant={isPrimaryContactMethod ? 'white' : 'outline-white'}
      rounded
      size="sm"
      onClick={() => updateDealerMetrics(dealer.boothId, contactMethodUrl)}
      style={{
        border: 'none',
      }}
    >
      <FontAwesomeIcon icon={contactMethodIcons[contactMethod]} size="2x" />
    </Button>
  );
};

export default ContactMethod;
